import React, { useMemo, FC, useContext, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import Pagination from '@atlaskit/pagination';
import {
  CapabilityDTO,
  CapabilitySummaryDTO,
  CapabilitySummaryDTOFromJSON,
} from '@atlassiansox/phobos-client-ts';
import EmptyState from '@atlaskit/empty-state';
import Badge from '@atlaskit/badge';
import { UFOExperience, ExperienceSuccess as UFOExperienceSuccess } from '@atlassian/ufo';

import { apiFetch } from '@/services';
import {
  useLocationController,
  useFetchErrorController,
  usePaginationController,
  filterStore,
  getSLOFilterQuery,
} from '@/controllers';
import { PrimaryType } from '@/meta';
import { mapParamsToAPIQuery } from '@/components/composed/catalog-search-bar/services/utils';
import { Params } from '@/components/composed/catalog-search-bar/types';
import {
  flexSpace,
  flexStart,
  spaceGapCompact,
  spaceTopComfortable,
  spaceTopCozy,
  tableExpandContentInnerStyles,
  tableHeadStyles,
  tableRowsStyles,
  textTransformCapitalize,
} from '@/styles';
import { querySpec } from '@/services/api-service/api-queries';
import ExternalLinkAction from '@/components/primitive/external-link';
import { BreachCount } from '@/components/primitive';
import { typeToExperience } from '@/common/ufo/experiences';

import { getPagesFromTotalPage } from '../../utils';
import { testIds } from '../../constants';
import * as stylesSearch from '../styles';
import SearchPageSkeleton from '../search-page-skeleton';
import SearchKeyValueTag from '../search-list-titles/search-key-value-tag';
import {
  capabilityResultRowStyles,
  capabilitySearchResultRowStyles,
} from '../../search-layouts/styles';

import * as styles from './styles';
import SearchListDashboardCapabilityItem from './search-list-dashboard-capability-item';

type SearchListBasicCardProps = {
  id: string;
  type: PrimaryType;
  isDashboard?: boolean;
  experienceSeq?: number;
};

/**
 * This is the component to display basic card, specifically: team & product
 */
const SearchListBasicCard: FC<SearchListBasicCardProps> = ({ id, type, experienceSeq }) => {
  const { onError } = useFetchErrorController();
  const { currentPage, onPageChange, setCurrentPage } = usePaginationController();
  const { state: filterState } = useContext(filterStore);
  const { isSearchPage } = useLocationController();
  const experience: UFOExperience = typeToExperience(type);

  // fetch content
  const capabilityFilter = isSearchPage ? '' : getSLOFilterQuery(filterState.slo);
  const capabilityParams = mapParamsToAPIQuery({ [type]: [{ publicId: id }] } as Params, true);
  const { queryKey, path } = querySpec('capability.report.{params}.{page}', {
    params: `${capabilityParams}${capabilityFilter}`,
    page: currentPage.toString(),
  });
  const { data, isInitialLoading: isContentLoading } = useQuery(queryKey, () => apiFetch(path), {
    onError,
  });

  const hasData = data?.content?.length > 0;
  const pages = useMemo(() => getPagesFromTotalPage(data?.totalPages), [data]);
  const showPagination = (data?.totalPages ?? 0) > 1;

  // fetch summary
  const { queryKey: queryKeySummary, path: pathSummary } = querySpec(
    'capability.summary.{params}',
    { params: `${capabilityParams}${capabilityFilter}` }
  );
  const { data: dataSummaryRaw, isInitialLoading: isSummaryLoading } = useQuery(
    queryKeySummary,
    () => apiFetch(pathSummary),
    { onError }
  );
  const dataSummary: CapabilitySummaryDTO = CapabilitySummaryDTOFromJSON(dataSummaryRaw);

  const isLoading: boolean = isSummaryLoading || isContentLoading;
  const showSummary: boolean = !!(!isSummaryLoading && dataSummary?.total);

  useEffect(() => {
    if (currentPage > 1) {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capabilityFilter]);

  return (
    <div css={isSearchPage && tableExpandContentInnerStyles}>
      <div css={styles.cardBasicContentTitleBarStyles}>
        <span css={[styles.cardBasicContentTitleStyles, styles.cardDashboardContentTitleStyles]}>
          Capabilities
        </span>
        {showSummary && (
          <span css={[flexStart, spaceGapCompact]}>
            <SearchKeyValueTag title="with open SLO breaches" isBadge>
              <BreachCount count={dataSummary.breaching} />
            </SearchKeyValueTag>
            <SearchKeyValueTag title="with SLO quality issues" isBadge>
              <BreachCount isWarning count={dataSummary.withQualityIssues} />
            </SearchKeyValueTag>
            <SearchKeyValueTag title="total" isBadge>
              <Badge
                max={Infinity}
                appearance="default"
                testId={testIds.searchResultSummaryTotalBadge}
              >
                {dataSummary.total}
              </Badge>
            </SearchKeyValueTag>
          </span>
        )}
        {isSearchPage && (
          <span css={textTransformCapitalize}>
            <ExternalLinkAction
              key={`${type}-link`}
              text={`View ${type} Page`}
              href={`/${type}/${id}?showDrafts=true`}
            />
          </span>
        )}
      </div>
      {isLoading && (
        <div css={[styles.cardSkeletonStyles, styles.cardBasicSkeletonStyles]}>
          <SearchPageSkeleton isCompact />
        </div>
      )}
      {!isLoading && !hasData && (
        <EmptyState header="" description={<>No capabilities found for the selected search</>} />
      )}
      {hasData && (
        <>
          <div
            css={[
              tableHeadStyles,
              flexSpace,
              spaceTopComfortable,
              capabilityResultRowStyles,
              isSearchPage && capabilitySearchResultRowStyles,
            ]}
          >
            <div>Title</div>
            <div />
            {!isSearchPage && <div>{type === 'team' ? 'Product' : 'Team'}</div>}
            <div>Capability Tier</div>
            <div>SLOs Breaching</div>
            <div>w/ Quality Issue</div>
            <div>Total</div>
          </div>
          <ul
            data-testid={testIds.searchResultListContentBasic}
            css={[
              spaceTopCozy,
              tableRowsStyles,
              stylesSearch.searchResultRowsStyles,
              showPagination ? '' : styles.cardBasicContentNoPaginationStyles,
            ]}
          >
            {data.content.map((capability: CapabilityDTO) => (
              <SearchListDashboardCapabilityItem
                capability={capability}
                key={capability.publicId}
              />
            ))}
          </ul>
          {showPagination && (
            <div css={spaceTopCozy}>
              <Pagination selectedIndex={currentPage - 1} pages={pages} onChange={onPageChange} />
            </div>
          )}
          {!isSearchPage && <UFOExperienceSuccess key={experienceSeq} experience={experience} />}
        </>
      )}
    </div>
  );
};

export default SearchListBasicCard;
